/**
 * Client Change Order Portal
 *
 * Public page for clients to view, approve, reject, or negotiate change orders.
 * No authentication required - access controlled via secure token.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useForm, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  AlertCircle,
  AlertTriangle,
  Calendar,
  Check,
  CheckCircle,
  Clock,
  DollarSign,
  Download,
  FileText,
  Loader2,
  MessageSquare,
  Paperclip,
  ThumbsDown,
  ThumbsUp,
  XCircle,
  Building2,
  FolderOpen,
} from 'lucide-react';
import {
  useGetClientChangeOrderQuery,
  useClientApproveMutation,
  useClientRejectMutation,
  useClientNegotiateMutation,
} from '../services/changeOrderApi';
import type {
  ClientChangeOrder,
  ChangeOrderType,
  ChangeOrderPriority,
  ChangeOrderStatus,
  ChangeOrderLineItem,
} from '../types/changeOrder';

// Validation schemas
const approveSchema = yup.object({
  signedBy: yup.string().required('Please enter your full name'),
  comments: yup.string().optional(),
});

const rejectSchema = yup.object({
  reason: yup.string().required('Please provide a reason for rejection'),
});

const negotiateSchema = yup.object({
  counterOffer: yup
    .number()
    .typeError('Please enter a valid amount')
    .required('Please enter your counter-offer amount')
    .min(0, 'Amount must be positive'),
  comments: yup.string().optional(),
});

type ApproveFormData = yup.InferType<typeof approveSchema>;
type RejectFormData = yup.InferType<typeof rejectSchema>;
type NegotiateFormData = yup.InferType<typeof negotiateSchema>;

// Helper functions
const formatCurrency = (amount: number, currency = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const getTypeLabel = (type: ChangeOrderType): string => {
  const labels: Record<ChangeOrderType, string> = {
    owner_requested: 'Owner Requested',
    unforeseen_condition: 'Unforeseen Condition',
    design_error: 'Design Error',
    scope_clarification: 'Scope Clarification',
    value_engineering: 'Value Engineering',
    allowance_adjustment: 'Allowance Adjustment',
  };
  return labels[type] || type;
};

const getPriorityLabel = (priority: ChangeOrderPriority): string => {
  const labels: Record<ChangeOrderPriority, string> = {
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    critical: 'Critical',
  };
  return labels[priority] || priority;
};

const getPriorityColor = (priority: ChangeOrderPriority): string => {
  switch (priority) {
    case 'critical':
      return 'text-red-600 bg-red-50 border-red-200';
    case 'high':
      return 'text-orange-600 bg-orange-50 border-orange-200';
    case 'medium':
      return 'text-amber-600 bg-amber-50 border-amber-200';
    case 'low':
    default:
      return 'text-gray-600 bg-gray-50 border-gray-200';
  }
};

const getStatusInfo = (
  status: ChangeOrderStatus
): { label: string; color: string; icon: React.ReactNode } => {
  switch (status) {
    case 'approved':
      return {
        label: 'Approved',
        color: 'text-green-600 bg-green-50 border-green-200',
        icon: <CheckCircle className="h-5 w-5" />,
      };
    case 'rejected':
      return {
        label: 'Rejected',
        color: 'text-red-600 bg-red-50 border-red-200',
        icon: <XCircle className="h-5 w-5" />,
      };
    case 'negotiating':
      return {
        label: 'Negotiating',
        color: 'text-purple-600 bg-purple-50 border-purple-200',
        icon: <MessageSquare className="h-5 w-5" />,
      };
    case 'pending_client':
    default:
      return {
        label: 'Pending Your Review',
        color: 'text-amber-600 bg-amber-50 border-amber-200',
        icon: <Clock className="h-5 w-5" />,
      };
  }
};

const getCostTypeLabel = (costType: string): string => {
  const labels: Record<string, string> = {
    labor: 'Labor',
    material: 'Material',
    equipment: 'Equipment',
    subcontractor: 'Subcontractor',
    other: 'Other',
  };
  return labels[costType] || costType;
};

// Signature Canvas Component
interface SignatureCanvasProps {
  onSignatureChange: (data: string | null) => void;
}

const SignatureCanvas: React.FC<SignatureCanvasProps> = ({ onSignatureChange }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas resolution for high DPI displays
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    // Set drawing styles
    ctx.strokeStyle = '#1f2937';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const getMousePos = (
    canvas: HTMLCanvasElement,
    evt: React.MouseEvent | React.TouchEvent
  ): { x: number; y: number } => {
    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in evt ? evt.touches[0].clientX : evt.clientX;
    const clientY = 'touches' in evt ? evt.touches[0].clientY : evt.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  };

  const startDrawing = (evt: React.MouseEvent | React.TouchEvent) => {
    evt.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const pos = getMousePos(canvas, evt);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (evt: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    evt.preventDefault();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const pos = getMousePos(canvas, evt);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    if (isDrawing && hasSignature) {
      const canvas = canvasRef.current;
      if (canvas) {
        onSignatureChange(canvas.toDataURL('image/png'));
      }
    }
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onSignatureChange(null);
  };

  return (
    <div className="space-y-2">
      <div className="relative border-2 border-dashed border-gray-300 rounded-lg bg-white">
        <canvas
          ref={canvasRef}
          className="w-full h-32 cursor-crosshair touch-none"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        {!hasSignature && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-sm text-gray-400">Sign here</p>
          </div>
        )}
      </div>
      {hasSignature && (
        <button
          type="button"
          onClick={clearSignature}
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          Clear signature
        </button>
      )}
    </div>
  );
};

// Main Component
const ClientChangeOrderPortal: React.FC = () => {
  const { accessToken } = useParams<{ accessToken: string }>();
  const [activeTab, setActiveTab] = useState<'approve' | 'reject' | 'negotiate' | null>(null);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [actionCompleted, setActionCompleted] = useState(false);
  const [actionType, setActionType] = useState<'approved' | 'rejected' | 'negotiating' | null>(
    null
  );

  // API calls
  const {
    data: response,
    isLoading,
    error,
    refetch,
  } = useGetClientChangeOrderQuery({ accessToken: accessToken || '' }, { skip: !accessToken });

  const [clientApprove, { isLoading: isApproving }] = useClientApproveMutation();
  const [clientReject, { isLoading: isRejecting }] = useClientRejectMutation();
  const [clientNegotiate, { isLoading: isNegotiating }] = useClientNegotiateMutation();

  // Form setup
  const approveForm = useForm<ApproveFormData>({
    resolver: yupResolver(approveSchema) as Resolver<ApproveFormData>,
  });

  const rejectForm = useForm<RejectFormData>({
    resolver: yupResolver(rejectSchema) as Resolver<RejectFormData>,
  });

  const negotiateForm = useForm<NegotiateFormData>({
    resolver: yupResolver(negotiateSchema) as Resolver<NegotiateFormData>,
  });

  const changeOrder = response?.data;
  const canTakeAction =
    changeOrder?.status === 'pending_client' || changeOrder?.status === 'negotiating';

  // Handlers
  const handleApprove = async (data: ApproveFormData) => {
    if (!accessToken) return;

    try {
      await clientApprove({
        accessToken,
        data: {
          signature: {
            signedBy: data.signedBy,
            signatureData: signatureData || undefined,
          },
          comments: data.comments,
        },
      }).unwrap();

      setActionCompleted(true);
      setActionType('approved');
      refetch();
    } catch {
      // Error is handled by RTK Query
    }
  };

  const handleReject = async (data: RejectFormData) => {
    if (!accessToken) return;

    try {
      await clientReject({
        accessToken,
        data: {
          reason: data.reason,
        },
      }).unwrap();

      setActionCompleted(true);
      setActionType('rejected');
      refetch();
    } catch {
      // Error is handled by RTK Query
    }
  };

  const handleNegotiate = async (data: NegotiateFormData) => {
    if (!accessToken) return;

    try {
      await clientNegotiate({
        accessToken,
        data: {
          counterOffer: data.counterOffer,
          comments: data.comments,
        },
      }).unwrap();

      setActionCompleted(true);
      setActionType('negotiating');
      refetch();
    } catch {
      // Error is handled by RTK Query
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-brand-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading change order...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !changeOrder) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center px-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Change Order Not Found</h1>
          <p className="text-gray-600 mb-6">
            This change order link may have expired or is no longer valid. Please contact the
            contractor for assistance.
          </p>
          <Link to="/" className="text-brand-600 hover:text-brand-700 font-medium">
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  // Action completed state
  if (actionCompleted && actionType) {
    const actionMessages = {
      approved: {
        title: 'Change Order Approved',
        message: 'Thank you for approving this change order. The contractor has been notified.',
        icon: <CheckCircle className="h-10 w-10 text-green-600" />,
        color: 'bg-green-100',
      },
      rejected: {
        title: 'Change Order Rejected',
        message: 'The change order has been rejected. The contractor has been notified.',
        icon: <XCircle className="h-10 w-10 text-red-600" />,
        color: 'bg-red-100',
      },
      negotiating: {
        title: 'Counter-Offer Submitted',
        message:
          'Your counter-offer has been submitted. The contractor will review and respond shortly.',
        icon: <MessageSquare className="h-10 w-10 text-purple-600" />,
        color: 'bg-purple-100',
      },
    };

    const msg = actionMessages[actionType];

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center px-4">
          <div
            className={`w-20 h-20 ${msg.color} rounded-full flex items-center justify-center mx-auto mb-6`}
          >
            {msg.icon}
          </div>
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">{msg.title}</h1>
          <p className="text-gray-600 mb-8">{msg.message}</p>
          <button
            onClick={() => setActionCompleted(false)}
            className="text-brand-600 hover:text-brand-700 font-medium"
          >
            View Change Order Details
          </button>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(changeOrder.status);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-brand-50 rounded-lg">
                <FileText className="h-6 w-6 text-brand-600" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">{changeOrder.coNumber}</h1>
                <p className="text-sm text-gray-500">{changeOrder.organization.name}</p>
              </div>
            </div>
            <div
              className={`flex items-center space-x-2 px-3 py-1.5 rounded-full border ${statusInfo.color}`}
            >
              {statusInfo.icon}
              <span className="text-sm font-medium">{statusInfo.label}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Change Order Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title & Description */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{changeOrder.title}</h2>
              <p className="text-gray-600 whitespace-pre-wrap">{changeOrder.description}</p>

              <div className="flex flex-wrap gap-3 mt-4">
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getPriorityColor(changeOrder.priority)}`}
                >
                  {getPriorityLabel(changeOrder.priority)} Priority
                </span>
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                  {getTypeLabel(changeOrder.type)}
                </span>
              </div>
            </div>

            {/* Project Info */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center space-x-3 mb-4">
                <FolderOpen className="h-5 w-5 text-gray-400" />
                <h3 className="text-sm font-medium text-gray-900">Project</h3>
              </div>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <Building2 className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{changeOrder.project.name}</p>
                  {changeOrder.project.number && (
                    <p className="text-sm text-gray-500">#{changeOrder.project.number}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Line Items */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-sm font-medium text-gray-900 mb-4">Cost Breakdown</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left text-xs font-medium text-gray-500 uppercase py-2 pr-4">
                        Description
                      </th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase py-2 px-4">
                        Type
                      </th>
                      <th className="text-right text-xs font-medium text-gray-500 uppercase py-2 px-4">
                        Qty
                      </th>
                      <th className="text-right text-xs font-medium text-gray-500 uppercase py-2 px-4">
                        Unit Cost
                      </th>
                      <th className="text-right text-xs font-medium text-gray-500 uppercase py-2 pl-4">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {changeOrder.pricing.lineItems.map((item: ChangeOrderLineItem, idx: number) => (
                      <tr key={idx}>
                        <td className="py-3 pr-4">
                          <p className="text-sm text-gray-900">{item.description}</p>
                          <p className="text-xs text-gray-500">{item.costCode}</p>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-xs text-gray-500">
                            {getCostTypeLabel(item.costType)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right text-sm text-gray-600">
                          {item.quantity} {item.unit}
                        </td>
                        <td className="py-3 px-4 text-right text-sm text-gray-600">
                          {formatCurrency(item.unitCost)}
                        </td>
                        <td className="py-3 pl-4 text-right text-sm font-medium text-gray-900">
                          {formatCurrency(item.extendedCost)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Subtotals */}
              <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Direct Cost</span>
                  <span className="text-gray-900">
                    {formatCurrency(changeOrder.pricing.directCost)}
                  </span>
                </div>
                {changeOrder.pricing.totalMarkup > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Markups</span>
                    <span className="text-gray-900">
                      {formatCurrency(changeOrder.pricing.totalMarkup)}
                    </span>
                  </div>
                )}
                {changeOrder.pricing.taxAmount && changeOrder.pricing.taxAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">
                      Tax ({(changeOrder.pricing.taxRate || 0) * 100}%)
                    </span>
                    <span className="text-gray-900">
                      {formatCurrency(changeOrder.pricing.taxAmount)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Schedule Impact */}
            {changeOrder.scheduleImpact?.hasImpact && (
              <div className="bg-amber-50 rounded-xl border border-amber-200 p-6">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-amber-900 mb-1">Schedule Impact</h3>
                    <p className="text-amber-800">
                      This change order will add{' '}
                      <span className="font-semibold">
                        {changeOrder.scheduleImpact.daysAdded} days
                      </span>{' '}
                      to the project schedule.
                    </p>
                    {changeOrder.scheduleImpact.description && (
                      <p className="mt-2 text-sm text-amber-700">
                        {changeOrder.scheduleImpact.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Attachments */}
            {changeOrder.attachments && changeOrder.attachments.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Paperclip className="h-5 w-5 text-gray-400" />
                  <h3 className="text-sm font-medium text-gray-900">Attachments</h3>
                </div>
                <div className="space-y-2">
                  {changeOrder.attachments.map((attachment: string, idx: number) => (
                    <a
                      key={idx}
                      href={attachment}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <Download className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-700">Attachment {idx + 1}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Summary & Actions */}
          <div className="space-y-6">
            {/* Amount Summary */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="text-center mb-6">
                <p className="text-sm text-gray-500 mb-1">
                  {changeOrder.pricing.isCredit ? 'Credit Amount' : 'Total Amount'}
                </p>
                <p
                  className={`text-3xl font-bold ${changeOrder.pricing.isCredit ? 'text-green-600' : 'text-gray-900'}`}
                >
                  {changeOrder.pricing.isCredit ? '-' : ''}
                  {formatCurrency(changeOrder.pricing.totalAmount)}
                </p>
              </div>

              <div className="space-y-3 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 flex items-center">
                    <Calendar className="h-4 w-4 mr-2" />
                    Request Date
                  </span>
                  <span className="text-gray-900">{formatDate(changeOrder.requestDate)}</span>
                </div>
                {changeOrder.requiredByDate && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 flex items-center">
                      <Clock className="h-4 w-4 mr-2" />
                      Required By
                    </span>
                    <span className="text-gray-900">{formatDate(changeOrder.requiredByDate)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            {canTakeAction && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-sm font-medium text-gray-900 mb-4">Your Response</h3>

                {!activeTab ? (
                  <div className="space-y-3">
                    <button
                      onClick={() => setActiveTab('approve')}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <ThumbsUp className="h-5 w-5" />
                      <span className="font-medium">Approve</span>
                    </button>
                    <button
                      onClick={() => setActiveTab('negotiate')}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      <DollarSign className="h-5 w-5" />
                      <span className="font-medium">Counter-Offer</span>
                    </button>
                    <button
                      onClick={() => setActiveTab('reject')}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-3 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      <ThumbsDown className="h-5 w-5" />
                      <span className="font-medium">Reject</span>
                    </button>
                  </div>
                ) : (
                  <div>
                    {/* Approve Form */}
                    {activeTab === 'approve' && (
                      <form
                        onSubmit={approveForm.handleSubmit(handleApprove)}
                        className="space-y-4"
                      >
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Full Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            {...approveForm.register('signedBy')}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            placeholder="Enter your full name"
                          />
                          {approveForm.formState.errors.signedBy && (
                            <p className="mt-1 text-sm text-red-500">
                              {approveForm.formState.errors.signedBy.message}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Signature
                          </label>
                          <SignatureCanvas onSignatureChange={setSignatureData} />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Comments (optional)
                          </label>
                          <textarea
                            {...approveForm.register('comments')}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
                            placeholder="Add any comments..."
                          />
                        </div>

                        <div className="flex space-x-3">
                          <button
                            type="button"
                            onClick={() => setActiveTab(null)}
                            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={isApproving}
                            className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                          >
                            {isApproving ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Check className="h-4 w-4" />
                            )}
                            <span>Approve</span>
                          </button>
                        </div>
                      </form>
                    )}

                    {/* Reject Form */}
                    {activeTab === 'reject' && (
                      <form onSubmit={rejectForm.handleSubmit(handleReject)} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Reason for Rejection <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            {...rejectForm.register('reason')}
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                            placeholder="Please explain why you are rejecting this change order..."
                          />
                          {rejectForm.formState.errors.reason && (
                            <p className="mt-1 text-sm text-red-500">
                              {rejectForm.formState.errors.reason.message}
                            </p>
                          )}
                        </div>

                        <div className="flex space-x-3">
                          <button
                            type="button"
                            onClick={() => setActiveTab(null)}
                            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={isRejecting}
                            className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                          >
                            {isRejecting ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <XCircle className="h-4 w-4" />
                            )}
                            <span>Reject</span>
                          </button>
                        </div>
                      </form>
                    )}

                    {/* Negotiate Form */}
                    {activeTab === 'negotiate' && (
                      <form
                        onSubmit={negotiateForm.handleSubmit(handleNegotiate)}
                        className="space-y-4"
                      >
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Your Counter-Offer Amount <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                              $
                            </span>
                            <input
                              type="number"
                              step="0.01"
                              {...negotiateForm.register('counterOffer')}
                              className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                              placeholder="0.00"
                            />
                          </div>
                          {negotiateForm.formState.errors.counterOffer && (
                            <p className="mt-1 text-sm text-red-500">
                              {negotiateForm.formState.errors.counterOffer.message}
                            </p>
                          )}
                          <p className="mt-1 text-xs text-gray-500">
                            Original amount: {formatCurrency(changeOrder.pricing.totalAmount)}
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Comments (optional)
                          </label>
                          <textarea
                            {...negotiateForm.register('comments')}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                            placeholder="Explain your counter-offer..."
                          />
                        </div>

                        <div className="flex space-x-3">
                          <button
                            type="button"
                            onClick={() => setActiveTab(null)}
                            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={isNegotiating}
                            className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                          >
                            {isNegotiating ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <DollarSign className="h-4 w-4" />
                            )}
                            <span>Submit Offer</span>
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Already responded message */}
            {!canTakeAction && (
              <div className="bg-gray-50 rounded-xl border border-gray-200 p-6 text-center">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${
                    changeOrder.status === 'approved'
                      ? 'bg-green-100'
                      : changeOrder.status === 'rejected'
                        ? 'bg-red-100'
                        : 'bg-gray-100'
                  }`}
                >
                  {changeOrder.status === 'approved' ? (
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  ) : changeOrder.status === 'rejected' ? (
                    <XCircle className="h-6 w-6 text-red-600" />
                  ) : (
                    <Clock className="h-6 w-6 text-gray-600" />
                  )}
                </div>
                <p className="text-sm text-gray-600">
                  {changeOrder.status === 'approved'
                    ? 'This change order has been approved.'
                    : changeOrder.status === 'rejected'
                      ? 'This change order has been rejected.'
                      : 'This change order is being processed.'}
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white mt-12">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <p>Powered by BuilderBel</p>
            <p>
              Sent on{' '}
              {changeOrder.clientApproval?.sentAt
                ? formatDate(changeOrder.clientApproval.sentAt)
                : '-'}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ClientChangeOrderPortal;
